import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Table, Input, Row, Col, Collapse, Button } from "reactstrap";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { getProducts } from "helpers/api_helper";

const RequiredMaterialsTable = ({
  neededProducts,
  title = "Total Raw Materials Needed",
  collapsed,                 // optional: controlled mode
  defaultCollapsed = false,  // optional: uncontrolled initial state
  onToggle,                  // optional: called with nextCollapsed
  maxTableHeight = 500,      // optional: customize scroll height
}) => {
  const { t } = useTranslation();

  // ---- Collapsible control: controlled or uncontrolled ----
  const [localCollapsed, setLocalCollapsed] = useState(!!defaultCollapsed);
  const isControlled = typeof collapsed === "boolean";
  const isCollapsed = isControlled ? collapsed : localCollapsed;

  const handleToggle = () => {
    const next = !isCollapsed;
    onToggle && onToggle(!next ? false : false); // keep signature (collapsed: boolean)
    if (!isControlled) setLocalCollapsed(!isCollapsed);
  };

  // ---- Existing state ----
  const [materialTotals, setMaterialTotals] = useState([]);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);
  const [showOnlyMissing, setShowOnlyMissing] = useState(false);

  // ---- Build totals (unchanged logic) ----
  useEffect(() => {
    const materialMap = {};

    const processMaterials = (materials, multiplier) => {
      materials.forEach(mat => {
        const quantity = parseFloat(mat.quantity || 0) * multiplier;

        if (mat.requirements && mat.requirements !== "[]") {
          try {
            const parsedReq = JSON.parse(mat.requirements || "[]");
            parsedReq.forEach(reqBlock => {
              if (Array.isArray(reqBlock.materials)) {
                processMaterials(reqBlock.materials, quantity);
              }
            });
          } catch (err) {
            console.warn(`Failed to parse sub-requirements for material ${mat.id}`, err);
          }
        } else {
          if (!materialMap[mat.id]) {
            materialMap[mat.id] = {
              id: mat.id,
              name: mat.name,
              quantity: quantity,
              stock: null,
              diff: null,
              unit: "",
            };
          } else {
            materialMap[mat.id].quantity += quantity;
          }
        }
      });
    };

    const calculateAll = async () => {
      neededProducts.forEach(product => {
        try {
          const topReq = JSON.parse(product.requirements || "[]");
          topReq.forEach(reqBlock => {
            if (Array.isArray(reqBlock.materials)) {
              processMaterials(reqBlock.materials, product.quantity);
            }
          });
        } catch (err) {
          console.warn(`Failed to parse product requirements for ${product.id}`, err);
        }
      });

      const totals = Object.values(materialMap);

      const withStock = await Promise.all(
        totals.map(async mat => {
          try {
            const fetched = await getProducts(mat.id);
            const data = fetched?.[0] || {};
            const stock = parseFloat(data.stock || 0);
            return {
              ...mat,
              stock,
              unit: data.unit || "",
              diff: stock - mat.quantity,
            };
          } catch (err) {
            console.error(`Failed to fetch stock for material ${mat.id}`, err);
            return {
              ...mat,
              stock: 0,
              unit: "",
              diff: -mat.quantity
            };
          }
        })
      );

      setMaterialTotals(withStock);
    };

    calculateAll();
  }, [neededProducts]);

  // ---- Sorting / filtering (memoized) ----
  const filtered = useMemo(() => {
    const list = materialTotals.filter(mat =>
      (!showOnlyMissing || mat.diff < 0) &&
      Object.values(mat).some(val =>
        String(val ?? "").toLowerCase().includes(search.toLowerCase())
      )
    );

    return list.sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      // numeric when both numbers; otherwise string compare
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);
      let cmp;
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        cmp = aNum - bNum;
      } else {
        cmp = String(aVal ?? "").toLowerCase().localeCompare(String(bVal ?? "").toLowerCase());
      }
      return sortAsc ? cmp : -cmp;
    });
  }, [materialTotals, search, showOnlyMissing, sortKey, sortAsc]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  return (
    <Card className="mb-4">
      {/* Header bar with collapse toggle */}
      <div
        className="d-flex align-items-center justify-content-between px-3 py-2"
        style={{ borderBottom: "1px solid #e9ecef" }}
      >
        <div className="d-flex align-items-center gap-2">
          <Button color="link" className="p-0 me-2" onClick={handleToggle}>
            {isCollapsed ? "▶" : "▼"}
          </Button>
          <h5 className="mb-0">
            {t(title)}{" "}
            <small className="text-muted">({materialTotals.length})</small>
          </h5>
        </div>

        {/* Quick summary on the right, stays visible even when collapsed */}
        <div className="text-muted" style={{ fontSize: 12 }}>
          {t("Missing")}: {materialTotals.filter(m => m.diff < 0).length}
        </div>
      </div>

      <Collapse isOpen={!isCollapsed}>
        <CardBody>
          <Row className="mb-2 align-items-center">
            <Col md="6">
              {/* left empty or extra filters if needed */}
            </Col>
            <Col md="6" className="text-end">
              <Input
                type="text"
                placeholder={t("Search materials...")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ maxWidth: 200, display: "inline-block", marginRight: 12 }}
              />
              <input
                id="only-missing"
                type="checkbox"
                checked={showOnlyMissing}
                onChange={() => setShowOnlyMissing(!showOnlyMissing)}
                style={{ marginRight: 6 }}
              />
              <label htmlFor="only-missing">{t("Show Only Missing")}</label>
            </Col>
          </Row>

          <div className="table-responsive" style={{ maxHeight: maxTableHeight, overflowY: "auto" }}>
            <Table bordered size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th onClick={() => toggleSort("id")} style={{ cursor: "pointer", whiteSpace:"nowrap" }}>
                    {t("Material ID")} {sortKey === "id" ? (sortAsc ? "↑" : "↓") : ""}
                  </th>
                  <th onClick={() => toggleSort("name")} style={{ cursor: "pointer", whiteSpace:"nowrap" }}>
                    {t("Name")} {sortKey === "name" ? (sortAsc ? "↑" : "↓") : ""}
                  </th>
                  <th onClick={() => toggleSort("quantity")} style={{ cursor: "pointer", whiteSpace:"nowrap" }}>
                    {t("Needed Qty")} {sortKey === "quantity" ? (sortAsc ? "↑" : "↓") : ""}
                  </th>
                  <th onClick={() => toggleSort("stock")} style={{ cursor: "pointer", whiteSpace:"nowrap" }}>
                    {t("Stock")} {sortKey === "stock" ? (sortAsc ? "↑" : "↓") : ""}
                  </th>
                  <th onClick={() => toggleSort("diff")} style={{ cursor: "pointer", whiteSpace:"nowrap" }}>
                    {t("Difference")} {sortKey === "diff" ? (sortAsc ? "↑" : "↓") : ""}
                  </th>
                  <th style={{ whiteSpace:"nowrap" }}>{t("Unit")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(mat => (
                  <tr key={mat.id}>
                    <td>{mat.id}</td>
                    <td>{mat.name}</td>
                    <td>{mat.quantity}</td>
                    <td>{mat.stock}</td>
                    <td style={{ color: mat.diff < 0 ? "red" : "inherit" }}>{mat.diff}</td>
                    <td>{mat.unit}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Collapse>
    </Card>
  );
};

RequiredMaterialsTable.propTypes = {
  neededProducts: PropTypes.array.isRequired,
  title: PropTypes.string,
  collapsed: PropTypes.bool,           // controlled mode
  defaultCollapsed: PropTypes.bool,    // uncontrolled initial state
  onToggle: PropTypes.func,            // (nextCollapsed:boolean) -> void
  maxTableHeight: PropTypes.number,
};

export default RequiredMaterialsTable;
